import { useMemo, useState } from 'react';
import { Calendar, Filter } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import type { Option } from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatClockTime, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { instituteToday } from '@/features/teacher/utils';
import { staffRecordApi } from '../api/staffLeave.api';
import { formatWorked, timeOf } from '../attendanceFormat';
import { ATTENDANCE_DAY_LABEL, ATTENDANCE_DAY_TONE } from '../constants';

/** How many months back the picker reaches. A year of history is what an office asks for. */
const MONTHS_BACK = 11;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * The months the picker offers, newest first.
 *
 * Generated from today rather than written out: a fixed list goes stale the
 * month after it is typed, and the one it replaced was already naming 2024.
 */
function recentMonths(): Option[] {
  // Counted back from the school's month, not the device's, so the list opens
  // on the month the institute is actually in.
  const [year, month] = instituteToday().split('-').map(Number);

  return Array.from({ length: MONTHS_BACK + 1 }, (_, index) => {
    const date = new Date(year, month - 1 - index, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    return { value, label: `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}` };
  });
}

/** One figure, drawn the way the other tabs on this profile draw theirs. */
function Tile({
  label,
  value,
  note,
  valueClass,
  children,
}: {
  label: string;
  value: string | number;
  note?: string;
  valueClass?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200/90 dark:border-slate-800 text-center shadow-2xs">
      <span className="text-xs font-bold text-slate-400 block uppercase">{label}</span>
      <span
        className={cn(
          'text-2xl font-black text-slate-900 dark:text-white mt-0.5 block',
          valueClass,
        )}
      >
        {value}
      </span>
      {note && <span className="text-[11px] text-slate-400 block mt-0.5">{note}</span>}
      {children}
    </div>
  );
}

export interface StaffAttendanceTabProps {
  /** The staff profile being read. Never this institute's own caller. */
  staffUuid: string;
}

/**
 * One member of staff's month, in the office.
 *
 * The same reader as the teacher's own punch card, so the two can never
 * disagree about what a day was: a granted day is leave here and leave there,
 * and neither shows it as an absence.
 *
 * Days with no punch are shown as well as days with one. An office asking
 * "was she in on the 14th" needs the answer for every day of the month, and a
 * log of only the days somebody remembered to sign would answer a different
 * question.
 */
export function StaffAttendanceTab({ staffUuid }: StaffAttendanceTabProps) {
  const monthOptions = useMemo(recentMonths, []);
  const [month, setMonth] = useState(monthOptions[0].value);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => staffRecordApi.attendanceMonth(staffUuid, month),
    [staffUuid, month],
  );

  const monthLabel =
    monthOptions.find((option) => option.value === month)?.label ?? month;

  /** Every day that has happened — a month is read backwards from today. */
  const log = useMemo(
    () => (data ? [...data.days].filter((day) => day.kind !== 'UPCOMING').reverse() : []),
    [data],
  );

  return (
    <div className="space-y-3.5 pt-1.5">
      <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
            <Calendar className="w-4 h-4 text-indigo-600" />
            <span>Monthly attendance summary</span>
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Showing the attendance log for{' '}
            <strong className="text-slate-900 dark:text-white">{monthLabel}</strong>
            {data && ` · a day counts as late after ${formatClockTime(data.dayStartsAt)}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-400" aria-hidden />
          <div className="w-52">
            <Select
              label="Attendance month"
              hideLabel
              hideSearch
              options={monthOptions}
              value={month}
              onChange={(event) => setMonth(event.target.value)}
              className="h-8 text-xs font-bold"
            />
          </div>
        </div>
      </div>

      {error && (
        <QueryError
          title="Could not load this attendance"
          error={error}
          onRetry={refetch}
        />
      )}

      {isLoading && (
        <div className="space-y-3.5" aria-busy="true">
          <span className="sr-only">Loading the attendance for {monthLabel}</span>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-[92px] rounded-2xl" />
            ))}
          </div>
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      )}

      {!isLoading && !error && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Tile
              label="Work days"
              value={data.summary.workingDaysSoFar}
              note={`${monthLabel}, so far`}
            />
            <Tile
              label="Days present"
              value={data.summary.present}
              valueClass="text-emerald-600 dark:text-emerald-400"
              note={
                data.summary.pending > 0
                  ? `${data.summary.pending} awaiting check`
                  : 'Checked by the office'
              }
            />
            <Tile
              label="Leaves taken"
              value={data.summary.leave}
              valueClass="text-indigo-600 dark:text-indigo-400"
              note="Approved leave"
            />
            <Tile
              label="Late arrivals"
              value={data.summary.late}
              valueClass="text-amber-600 dark:text-amber-400"
              note={`After ${formatClockTime(data.dayStartsAt)}`}
            />
            <Tile
              label="Attendance rate"
              value={data.summary.attendanceRate === null ? '—' : `${data.summary.attendanceRate}%`}
              valueClass="text-emerald-600 dark:text-emerald-400"
            >
              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full mt-1 overflow-hidden">
                <div
                  className="bg-emerald-500 h-full rounded-full"
                  style={{ width: `${data.summary.attendanceRate ?? 0}%` }}
                />
              </div>
            </Tile>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2.5">
              <h4 className="text-xs font-extrabold text-slate-900 dark:text-white">
                Daily check-in &amp; punch logs ({monthLabel})
              </h4>
            </div>

            {log.length === 0 ? (
              <EmptyState
                icon={<Calendar className="h-5 w-5" />}
                title="Nothing to show for this month"
                description="No working day of this month has passed yet, so there is no attendance to read."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs sm:text-sm">
                  <thead>
                    <tr className="bg-slate-100 dark:bg-[#0b1322] border-b border-slate-200 dark:border-slate-800 text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300">
                      <th className="py-3 px-3">Date</th>
                      <th className="py-3 px-3">In time</th>
                      <th className="py-3 px-3">Out time</th>
                      <th className="py-3 px-3">Total working hours</th>
                      <th className="py-3 px-3 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {log.map((day) => (
                      <tr
                        key={day.date}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      >
                        <td className="py-2.5 px-3 font-bold text-slate-900 dark:text-white">
                          {formatDate(day.date)}{' '}
                          <span className="font-semibold text-slate-400">
                            ({day.weekday.slice(0, 3)})
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-mono font-semibold text-slate-700 dark:text-slate-300">
                          {day.punchedInAt ? formatClockTime(timeOf(day.punchedInAt)) : '—'}
                        </td>
                        <td className="py-2.5 px-3 font-mono font-semibold text-slate-700 dark:text-slate-300">
                          {day.punchedOutAt ? formatClockTime(timeOf(day.punchedOutAt)) : '—'}
                          {day.isAutoPunchOut && (
                            <span className="ml-1 font-sans text-[11px] font-semibold text-ink-3">auto</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 font-semibold text-slate-700 dark:text-slate-300">
                          {formatWorked(day.workedMinutes)}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          <span
                            className={cn(
                              'px-2.5 py-0.5 rounded-full text-xs font-bold',
                              ATTENDANCE_DAY_TONE[day.kind],
                            )}
                          >
                            {ATTENDANCE_DAY_LABEL[day.kind]}
                          </span>
                          {day.isLate && (
                            <span className="ml-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400">
                              Late
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
